// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Beeline — colour system + WCAG contrast helper.
//
// The play surface is a flat vector world: land plates on a deep slate sea, with
// guessed countries flooded by a cold→hot warmth ramp. Colour is a SUPPLEMENTARY
// cue only — every guess also shows its exact distance, an arrow and a proximity
// percentage — but the ramp still has to be visible (principle #22: an invisible
// piece reads as atmosphere), so every meaningful colour is pinned by
// tests/contrast.test.ts.

export const PALETTE = {
  // map
  sea: '#0c1c28',
  seaDeep: '#081521',
  land: '#2a3a48', // unguessed country fill
  landBorder: '#486072', // the stroke that delineates land from sea + neighbours

  // panels / chrome
  panel: '#12293a',
  panelDeep: '#0e2130',

  // text
  text: '#e8f0f4',
  textMuted: '#a9bcc8',
  ink: '#0c1c28', // dark glyph/number drawn ON a bright heat fill

  // warmth ramp (guessed countries) — cold → hot
  cold: '#7fa2b4',
  cool: '#3fc3b6',
  mild: '#f6c85f',
  warm: '#f5824f',
  hot: '#ff6b64',

  // feedback
  solved: '#46cf72', // the mystery, revealed
  accent: '#ffd166', // the beeline arrow / target ring
  danger: '#ff6b64',
} as const;

/** The warmth ramp in order, coldest first — what contrast.test.ts iterates. */
export const HEAT_RAMP = [
  PALETTE.cold,
  PALETTE.cool,
  PALETTE.mild,
  PALETTE.warm,
  PALETTE.hot,
] as const;

/**
 * Bucket a proximity percentage into a ramp colour. Discrete (not interpolated)
 * so every colour that can appear on the map is a named constant the contrast
 * test can pin.
 */
export function heatColor(pct: number, solved = false): string {
  if (solved) return PALETTE.solved;
  if (pct >= 75) return PALETTE.hot;
  if (pct >= 55) return PALETTE.warm;
  if (pct >= 35) return PALETTE.mild;
  if (pct >= 18) return PALETTE.cool;
  return PALETTE.cold;
}

/* ── WCAG contrast ────────────────────────────────────────────────────────── */

function channel(v: number): number {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

/** Relative luminance of a #rrggbb colour (WCAG 2.1). */
export function luminance(hex: string): number {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
  if (!m) throw new Error(`not a hex colour: ${hex}`);
  const [r, g, b] = [m[1], m[2], m[3]].map((h) => channel(parseInt(h, 16)));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two #rrggbb colours (1..21, symmetric). */
export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
